import { ParsedUrlQuery } from 'querystring';

import { GetStaticPaths, GetStaticProps } from 'next';
import Image from 'next/image';

import { SignPost } from '@common/SignPost';
import { H3, H5 } from '@common/headline';

import ArticleTile from '@components/Resources/Home/ArticleTile';
import CategoryNavigation from '@components/Resources/Category/CategoryNavigation';
import Meta from '@components/Meta';
import { NavBar } from '@components/NavBar/NavBar';
import { Footer } from '@components/Footer/Footer';
import { ReferencesRibbon } from '@common/ReferencesRibbon';

import {
  combinePostsForCountryVariants,
  getAuthorURL,
  getEmptyStaticPaths,
  getRecentArticles,
  postGuidesQuery,
} from '@nextUtils/guidesUtils';

import {
  ArticleCategoriesType,
  RecentArticleEdgeType,
  CategoryFieldsFragment,
} from '@nextTypes/guides';

import { ErrorTracking } from '@common/errorTracking';

type Props = {
  allCategories: ArticleCategoriesType;
  authorImageUrl: string;
  bioHtml: string;
  fullName: string;
  metaDescription: string;
  recentPosts: {
    edges: RecentArticleEdgeType[];
  };
  title: string;
};

const POSTS_PER_PAGE = 4;

export const getStaticPaths: GetStaticPaths = getEmptyStaticPaths;

interface Params extends ParsedUrlQuery {
  firstName: string;
}

export const getStaticProps: GetStaticProps<Props, Params> = async ({
  params,
  locale,
}) => {
  try {
    const query = `
      ${CategoryFieldsFragment}
      query {
        allCategories: categories(first: 100, where: {shouldOutputInFlatList: true}) {
          edges {
            node {
              ...CategoryFields
            }
          }
        }
        users(where: {nicename: "${
          params?.firstName === 'fiona' ? 'fi' : params?.firstName
        }"}) {
          edges {
            node {
              name
              description
              avatar {
                url
              }
            }
          }
        }
        recentPosts: posts(first: ${POSTS_PER_PAGE * 2},
          where: {
            ${
              params?.firstName === 'fiona'
                ? ''
                : `authorName: "${params?.firstName}",`
            }
            categoryNotIn: [1, 56, 57], # 1 = Uncategorised, 56 = Stories, 57 = Local
            orderby:{
              field:DATE
              order:DESC
            }
            tagSlugIn: [ "all_countries", "${locale}" ]
          }
        ) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            cursor
            node {
              title
              slug
              seo {
                metaDesc
              }
              author {
                name
                avatar {
                  url
                }
              }
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
      }
    `;

    const json = await postGuidesQuery(query);

    const users = json.data.users;

    if (!users || users.edges.length === 0) {
      return {
        notFound: true,
      };
    }

    const userNode = users.edges[0].node;

    const bio = userNode.description;

    if (!bio) {
      // Only reveal WP users with a description
      return {
        notFound: true,
      };
    }

    const bioHtml = `<p>${bio
      .replace(/\r/g, '')
      .replace(/\n\n/g, '</p><p>')}</p>`;

    const fullName = userNode.name;
    const title = `${fullName} | Author | Second Nature Guides`;

    // Use 'founders' description for user 'Chris and Mike'
    const metaDescription = `Biography and latest articles for our ${
      fullName.includes(' and ') ? 'founders' : 'health writer'
    } ${fullName}.`;

    // Use higher res version instead of default 96x96
    const authorImageUrl = userNode.avatar.url.replace('s=96', 's=400');

    return {
      props: {
        allCategories: json.data.allCategories,
        authorImageUrl,
        bioHtml,
        fullName,
        metaDescription,
        recentPosts: combinePostsForCountryVariants(
          json.data.recentPosts,
          POSTS_PER_PAGE,
        ),
        title,
      },
      // Refresh this page at most every 300 seconds
      // If a request comes in within 300 seconds, it will serve the existing cached page
      // If a request comes in outside of 300 seconds, it will serve the cached page - but re-fetch the page in the background
      revalidate: 300,
    };
  } catch (err) {
    ErrorTracking.track(err);
    // We do not handle this error, but it is handled by NextJS https://github.com/vercel/next.js/discussions/11180#discussioncomment-2399
    throw err;
  }
};

const Author = ({
  allCategories,
  authorImageUrl,
  bioHtml,
  fullName,
  metaDescription,
  recentPosts,
  title,
}: Props): JSX.Element => {
  const recentArticleTiles = getRecentArticles(recentPosts).map(article => (
    <ArticleTile
      title={article.title}
      category={article.category}
      href={article.href}
      imageUrl={article.featuredImageUrl}
      key={article.title}
      description={article.description}
      date={article.date}
      columnsPerRowDesktop={2}
    />
  ));

  // Treat 'Chris and Mike' as a first name
  const firstName = fullName.includes(' and ')
    ? fullName
    : fullName.split(' ')[0];

  const authorSlug = firstName.replace(' and ', '-and-').toLowerCase();

  return (
    <>
      <Meta
        title={title}
        metaDescription={metaDescription}
        relativeCanonicalURL={getAuthorURL(fullName)}
      />
      <NavBar utmMedium="author" />
      <CategoryNavigation allCategories={allCategories} />
      <div className="px-3 lg:container lg:m-auto lg:px-0">
        <div className="mx-auto mt-[50px] w-[100px]">
          <Image
            className="rounded-[50%]"
            src={authorImageUrl}
            alt={fullName}
            width={100}
            height={100}
          />
        </div>
        <div className="mt-[30px] text-center uppercase text-dawn-blue">
          <H5>Author</H5>
        </div>
        <div className="mt-[10px] text-center text-blue">
          <H3>{fullName}</H3>
        </div>
        <div className="mt-[20px] text-left text-blue md:mx-[10px]">
          <div dangerouslySetInnerHTML={{ __html: bioHtml }} />
        </div>
        <div className="mb-[30px] mt-[50px] text-blue md:mx-[10px]">
          <H3>
            <span className="border-b-4 border-dawn-blue">
              {firstName}’s Latest Articles
            </span>
          </H3>
        </div>
        <div className="mb-[100px] flex flex-wrap justify-between">
          {recentArticleTiles}
        </div>
      </div>
      <SignPost
        version="A"
        utmMedium="author"
        utmCampaign={`${authorSlug}_banner`}
      />
      <ReferencesRibbon />
      <Footer />
    </>
  );
};

export default Author;
